import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC, ReactElement } from 'react';

import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { FavoriteFilled as FavouriteIcon } from '@vgno/icons';

import { isTouchDevice } from '../../../utils/device';
import { trackNavigationClick } from '../../../shared/helpers/trackingHelpers';
import type { MenuItem } from '../helpers';

import * as styles from './DropdownMenu.module.scss';

type Props = {
  label: string | ReactElement;
  items: MenuItem[];
  labelClassName?: string;
  variant?: 'middle' | 'right';
};

export const DropdownMenu: FC<Props> = ({
  label,
  items,
  labelClassName,
  variant = 'middle',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null!);

  const onTouchStart = useCallback(
    () => isTouchDevice && setIsOpen(!isOpen),
    [isOpen]
  );

  useEffect(() => {
    function onDocumentTouchStart(event: TouchEvent) {
      if (!containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('touchstart', onDocumentTouchStart);

      return () =>
        document.removeEventListener('touchstart', onDocumentTouchStart);
    }
  }, [isOpen]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.isOpen]: isOpen,
      })}
      ref={containerRef}
    >
      {/* we don't want this item to be clickable, yet it has to be accessible using tab */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      <span
        className={classNames(labelClassName, styles.label)}
        tabIndex={0}
        onTouchStartCapture={onTouchStart}
      >
        {label}
      </span>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
      <ul
        className={classNames(styles.menu, {
          [styles.rightVariant]: variant === 'right',
        })}
      >
        {items.map(({ label, link, onClick, id, type }, index) => (
          <li key={label}>
            {link ? (
              <NavLink
                to={link}
                className={styles.itemContent}
                activeClassName={styles.itemContentActive}
                onClick={(event) => {
                  // blur to get around the :focus-within state and hide the submenu
                  trackNavigationClick({
                    label: `Mer>${label}`,
                    link,
                    id,
                    type,
                  });
                  event.currentTarget.blur();

                  if (isTouchDevice) {
                    setIsOpen(false);
                  }
                }}
              >
                {label}
              </NavLink>
            ) : (
              <button className={styles.itemContent} onClick={onClick}>
                {id === 'favourites' && <FavouriteIcon />}
                {label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
